import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";

// Auth & Public
import Login from "./components/newOnboarding/screens/login/login";
import SignUp from "./components/newOnboarding/screens/SignUp/signUp";
import ForgotPassword from "./components/newOnboarding/screens/ForgotPassword/forgotPassword";
import EmailVerification from "./components/auth/email-verification/emailVerification";
import NotFound404 from "./Shared/notFound404";
import EmailVerificationFailed from "./components/auth/email-verification-failed/emailVerificationFailed";
import ResendEmailSuccess from "./components/auth/email-verification-failed/resend-email-success/resendEmailSuccess";
import PasswordResetLinkExpiry from "./components/auth/password-reset-link-expiry/passwordResetLinkExpiry";
import PasswordResetLinkSuccess from "./components/auth/password-reset-link-expiry/password-reset-success/passwordResetLinkSuccess";

// Onboarding & App
import ProtectedRoutes from "./protectedRoutes";
import { isAuthSessionValid, needsOrganisationSetup } from "./utils/authSession";
import CompleteOrganisation from "./components/newOnboarding/screens/completeOrganisation/completeOrganisation";
import AppCreationSuccess from "./components/onboarding/app-creation-success/appCreationSuccess";
import Application from "./components/application/application";
import Dashboard from "./components/application/dashboard/dashboard";
import Report from "./components/application/report/report";
import ManageApps from "./components/application/manage-apps/manageApps";
import AppInfo from "./components/application/manage-apps/app-info/appInfo";
import AppSettings from "./components/application/manage-apps/app-settings/app-settings";
import ProductCatalog from "./components/application/product-catalog/productCatalog";
import PaymentStatusCallback from "./components/application/billing/payment-status-callback/paymentStatusCallback";
import BatchService from "./components/application/batch-service/batch-service";
import TrialCenter from "./components/application/trial-center/trial-center";
import Docs from "./components/application/docs/docs";
import MyAccount from "./components/application/my-account/myAccount";
import ActivationForm from "./components/application/activation/activationForm";

// Context
import OnBoardingSidebarContextProvider from "./utils/onboardingSidebarContext";
import EnvironmentContextProvider from "./context/environmentContext";
import ResetPassword from "./components/newOnboarding/screens/ForgotPassword/resetPassword";
import Billing from "./components/application/new-billing/billing";

const LoginRoute: React.FC = () =>
  isAuthSessionValid() ? (
    <Navigate
      to={
        needsOrganisationSetup()
          ? "/complete-organisation"
          : "/application/dashboard"
      }
      replace
    />
  ) : (
    <Login />
  );

const RequireOrganisation: React.FC = () =>
  needsOrganisationSetup() ? (
    <Navigate to="/complete-organisation" replace />
  ) : (
    <Outlet />
  );

const DashboardRouter: React.FC = () => {
  return (
    <OnBoardingSidebarContextProvider>
      <EnvironmentContextProvider>
        <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route
            path="/password-reset-link-expiry"
            element={<PasswordResetLinkExpiry />}
          />
          <Route
            path="/password-reset-link-success"
            element={<PasswordResetLinkSuccess />}
          />
          <Route
            path="/email-verification-failed"
            element={<EmailVerificationFailed />}
          />
          <Route
            path="/resend-email-success"
            element={<ResendEmailSuccess />}
          />
          <Route path="/auth/reset-password" element={<ResetPassword />} />

          {/* Onboarding */}
          {/* <Route
            path="/onboarding"
            element={<Navigate to="/onboarding/create-org" replace />}
          /> */}

          <Route element={<ProtectedRoutes />}>
            <Route
              path="/complete-organisation"
              element={<CompleteOrganisation />}
            />
            <Route element={<RequireOrganisation />}>
            {/* Home — authenticated users stay at / */}
            <Route path="/" element={<Application />}>
              <Route index element={<Dashboard />} />
            </Route>

            {/* <Route path="/onboarding/:value" element={<Onboarding />} /> */}
            <Route path="/welcome" element={<AppCreationSuccess />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/activation-form" element={<ActivationForm />} />
            <Route path="/doc" element={<Docs />} />

            {/* Application Layout + Nested Routes */}
            <Route path="/application" element={<Application />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="reports" element={<Report />} />
              <Route path="manage-apps" element={<ManageApps />} />
              <Route path="manage-apps/:appId" element={<AppInfo />} />
              <Route
                path="manage-apps/:appId/settings"
                element={<AppSettings />}
              />
              <Route
                path="products"
                element={<Navigate to="product-catalog" replace />}
              />
              <Route path="product-catalog" element={<ProductCatalog />} />
              <Route path="billing" element={<Billing />} />
              <Route
                path="billing/callback"
                element={<PaymentStatusCallback />}
              />
              <Route path="batch-service" element={<BatchService />} />
              <Route path="trial-center" element={<TrialCenter />} />
            </Route>
            </Route>
          </Route>

          {/* 404 Fallback */}
          <Route path="*" element={<NotFound404 />} />
        </Routes>
        </Router>
      </EnvironmentContextProvider>
    </OnBoardingSidebarContextProvider>
  );
};

export default DashboardRouter;
